const SKIP_KEYS = new Set(['id', '_destroy']);

/** Symbol returned when `path` is not present on `obj` (distinct from `null`). */
export const NESTED_UNSET = Symbol('nestedUnset');

/** Like getNested but only when each segment exists via own property (null is a real value). */
export function getNestedIfOwn(obj, path) {
  if (obj == null || typeof obj !== 'object') return NESTED_UNSET;
  if (!path.includes('.')) {
    return Object.prototype.hasOwnProperty.call(obj, path) ? obj[path] : NESTED_UNSET;
  }
  let cur = obj;
  for (const key of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return NESTED_UNSET;
    if (!Object.prototype.hasOwnProperty.call(cur, key)) return NESTED_UNSET;
    cur = cur[key];
  }
  return cur;
}

export function hasUnsyncedAtPath(unsynced, fieldId) {
  return getNestedIfOwn(unsynced, fieldId) !== NESTED_UNSET;
}

/**
 * Flatten a patch/snapshot into dot-path field ids (e.g. `quote_lines.0.price`).
 * Skips `id` and `_destroy` (used for Rails nested attrs, not form fields).
 */
export function flattenSnapshotKeys(obj, prefix = '') {
  if (obj == null || typeof obj !== 'object') return [];
  if (Array.isArray(obj)) {
    return obj.flatMap((item, i) => flattenSnapshotKeys(item, `${prefix}${i}.`));
  }
  return Object.keys(obj).flatMap((key) => {
    if (SKIP_KEYS.has(key)) return [];
    const path = prefix ? `${prefix}${key}` : key;
    const val = obj[key];
    if (val != null && typeof val === 'object') {
      return flattenSnapshotKeys(val, `${path}.`);
    }
    return [path];
  });
}

export const EMPTY_FIELD_SYNC_STATE = { isSaving: false, isSaved: false };

/** Client-only id for quote lines not yet persisted (timestamp, unique per call). */
export function newQuoteLineId() {
  return Date.now();
}

export function applyQuoteLineIdRemap(lines, idRemap) {
  if (!Array.isArray(lines) || !idRemap || !Object.keys(idRemap).length) return lines;
  return lines.map((line) => {
    if (!line || line.id == null) return line;
    const realId = idRemap[String(line.id)];
    return realId != null ? { ...line, id: realId } : line;
  });
}

/** Assign timestamp ids to new line rows before sending nested attrs to the API. */
export function ensureLineIdsInQuotePatch(patch) {
  if (!patch?.quote_lines || !Array.isArray(patch.quote_lines)) return patch;
  const base = Date.now();
  let seq = 0;
  return {
    ...patch,
    quote_lines: patch.quote_lines.map((line) => {
      if (!line || line._destroy || line.id != null) return line;
      return { ...line, id: base + (seq += 1) };
    }),
  };
}

function normalizeQuoteLinesArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    return Object.keys(raw)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => raw[key])
      .filter((line) => line != null && typeof line === 'object');
  }
  return [];
}

/**
 * Merge saved quote lines with unsynced overlay, then sort by +position+.
 * New drafts only grow the row count when unsynced has more rows than the server
 * (e.g. + Add line item); otherwise overlay edits stay within server row count.
 */
export function mergeQuoteLines(activeQuote, unsynced) {
  const q = normalizeQuoteLinesArray(activeQuote?.quote_lines);
  const u = Array.isArray(unsynced?.quote_lines) ? unsynced.quote_lines : null;
  if (!u || u.length === 0) return sortQuoteLinesByPosition(q);

  const isNewDraft = activeQuote?.id == null || activeQuote?.id === '';
  const maxLen = isNewDraft
    ? (u.length > q.length ? u.length : q.length)
    : Math.max(q.length, u.length);

  const merged = [];
  for (let i = 0; i < maxLen; i += 1) {
    merged.push({
      ...(q[i] && typeof q[i] === 'object' ? q[i] : {}),
      ...(u[i] && typeof u[i] === 'object' ? u[i] : {}),
    });
  }
  return sortQuoteLinesByPosition(merged);
}

export function sortQuoteLinesByPosition(lines) {
  return lines
    .map((line, index) => ({ line, index }))
    .sort((a, b) => {
      const la = a.line;
      const lb = b.line;
      if (la?._destroy && !lb?._destroy) return 1;
      if (!la?._destroy && lb?._destroy) return -1;
      const pa =
        la?.position != null && la.position !== '' ? Number(la.position) : a.index;
      const pb =
        lb?.position != null && lb.position !== '' ? Number(lb.position) : b.index;
      if (pa !== pb) return pa - pb;
      return a.index - b.index;
    })
    .map(({ line }) => line);
}

export function quoteLineSortId(line, fallbackIndex) {
  if (line?.id != null) return String(line.id);
  return `idx-${fallbackIndex}`;
}

/** Assign 0-based positions to non-destroyed rows; destroyed rows keep their position. */
export function assignQuoteLinePositions(lines) {
  let pos = 0;
  return lines.map((line) => {
    if (!line || line._destroy) return line;
    const next = { ...line, position: pos };
    pos += 1;
    return next;
  });
}
